import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import SpreadDiagram from '../../tarot-components/SpreadDiagram'
import CardBack from '../../tarot-components/CardBack'
import { SPREAD_TYPES } from '../../tarot-data/cards'
import { useSpreadLocalizer } from '../../i18n/useLocalized'
import { track } from './landingUtils'

const NUMERALS = ['I', 'II', 'III']

// One engraved glyph per step, drawn in the astrolabe's line language
const STEP_GLYPHS = [
    // Choose: three cards laid out
    <svg key="choose" viewBox="0 0 64 40" fill="none" stroke="currentColor" strokeWidth="1.2">
        <rect x="4" y="6" width="16" height="28" rx="2" />
        <rect x="24" y="4" width="16" height="28" rx="2" className="glyph-accent" />
        <rect x="44" y="6" width="16" height="28" rx="2" />
    </svg>,
    // Shuffle: a stacked deck, the top card lifted
    <svg key="shuffle" viewBox="0 0 64 40" fill="none" stroke="currentColor" strokeWidth="1.2">
        <rect x="18" y="10" width="20" height="28" rx="2" />
        <rect x="21" y="8" width="20" height="28" rx="2" />
        <rect x="27" y="2" width="20" height="28" rx="2" transform="rotate(12 37 16)" className="glyph-accent" />
    </svg>,
    // Listen: rings spreading from a diamond
    <svg key="listen" viewBox="0 0 64 40" fill="none" stroke="currentColor" strokeWidth="1.2">
        <circle cx="32" cy="20" r="8" />
        <circle cx="32" cy="20" r="13" opacity="0.6" />
        <circle cx="32" cy="20" r="18" opacity="0.3" />
        <rect x="29.5" y="17.5" width="5" height="5" transform="rotate(45 32 20)" className="glyph-fill" />
    </svg>,
]

export const HowItWorks = () => {
    const { t } = useTranslation()
    const steps = t('public.how.steps', { returnObjects: true })

    return (
        <section className="arcana-section" aria-labelledby="how-heading">
            <div className="arcana__inner">
                <header className="arcana__section-head reveal">
                    <p className="arcana__eyebrow">{t('public.how.eyebrow')}</p>
                    <h2 id="how-heading" className="arcana__h2">{t('public.how.heading')}</h2>
                </header>

                <ol className="arcana__grid arcana__grid--steps">
                    {Array.isArray(steps) && steps.map((step, i) => (
                        <li key={NUMERALS[i]} className="arcana-card reveal">
                            <div className="arcana-card__top">
                                <span className="arcana-card__numeral" aria-hidden="true">{NUMERALS[i]}</span>
                                <span className="arcana-card__glyph" aria-hidden="true">{STEP_GLYPHS[i]}</span>
                            </div>
                            <span className="arcana-card__numeral-rule" aria-hidden="true" />
                            <h3 className="arcana-card__title">{step.title}</h3>
                            <p className="arcana-card__text">{step.text}</p>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    )
}

// Shortest first, so the row reads as a path from a quick question to the
// deepest one
const SPREAD_ORDER = ['one-card', 'three-card', 'career', 'relationship', 'celtic-cross']

export const SpreadShowcase = () => {
    const { t } = useTranslation()
    const localize = useSpreadLocalizer()
    const spreads = SPREAD_ORDER
        .map((id) => Object.values(SPREAD_TYPES).find((s) => s.id === id))
        .filter(Boolean)
        .map(localize)

    return (
        <section className="arcana-section" aria-labelledby="spreads-heading">
            <div className="arcana__inner">
                <header className="arcana__section-head reveal">
                    <p className="arcana__eyebrow">{t('public.spreads.eyebrow')}</p>
                    <h2 id="spreads-heading" className="arcana__h2">{t('public.spreads.heading')}</h2>
                </header>

                <ul className="spreads-row">
                    {spreads.map((spread) => (
                        <li key={spread.id} className="spread-plate reveal">
                            <div className="spread-plate__diagram">
                                <SpreadDiagram spreadId={spread.id} />
                            </div>
                            <p className="spread-plate__count">
                                {t('spreads.cardCount', { count: spread.positions.length })}
                            </p>
                            <h3 className="spread-plate__name">{spread.name}</h3>
                            <p className="spread-plate__text">{t(`public.spreads.bestFor.${spread.id}`)}</p>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}

export const Faq = () => {
    const { t } = useTranslation()
    const items = t('public.faq.items', { returnObjects: true })

    return (
        <section className="arcana-section" aria-labelledby="faq-heading">
            <div className="arcana__inner arcana__inner--narrow">
                <header className="arcana__section-head reveal">
                    <p className="arcana__eyebrow">{t('public.faq.eyebrow')}</p>
                    <h2 id="faq-heading" className="arcana__h2">{t('public.faq.heading')}</h2>
                </header>

                <div className="faq reveal">
                    {Array.isArray(items) && items.map(({ q, a }) => (
                        <details key={q} className="faq__item">
                            <summary className="faq__q">
                                {q}
                                <span className="faq__icon" aria-hidden="true" />
                            </summary>
                            <p className="faq__a">{a}</p>
                        </details>
                    ))}
                </div>
            </div>
        </section>
    )
}

export const FinalCta = () => {
    const { t } = useTranslation()

    return (
        <section className="arcana-section final" aria-labelledby="final-heading">
            <div className="arcana__inner final__inner reveal">
                <div className="final__deck" aria-hidden="true">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <span key={i} className="final__card" style={{ '--off': i - 2 }}>
                            <CardBack />
                        </span>
                    ))}
                </div>
                <h2 id="final-heading" className="arcana__h2 final__heading">{t('public.final.heading')}</h2>
                <p className="arcana__section-text">{t('public.final.text')}</p>
                <div className="arcana__actions">
                    <Link
                        to="/register"
                        className="arcana-btn"
                        onClick={() => track('cta_click', { where: 'final' })}
                    >
                        {t('public.final.cta')}
                        <span className="arcana-btn__arrow" aria-hidden="true">&#8594;</span>
                    </Link>
                </div>
            </div>
        </section>
    )
}

export const LandingFooter = () => {
    const { t } = useTranslation()

    return (
        <footer className="arcana__footer">
            <div className="arcana__footer-row">
                <p className="arcana__footer-brand">
                    <span className="arcana-nav__mark" aria-hidden="true" />
                    askarkana
                    <span className="arcana__footer-tagline">{t('public.footer.tagline')}</span>
                </p>
                <nav className="arcana__footer-links">
                    <Link to="/login" className="arcana__link">{t('public.nav.signIn')}</Link>
                    <Link to="/register" className="arcana__link">{t('public.nav.getStarted')}</Link>
                    <Link to="/privacy" className="arcana__link">{t('public.footer.privacy')}</Link>
                    <Link to="/terms" className="arcana__link">{t('public.footer.terms')}</Link>
                </nav>
            </div>
            <p className="arcana__footer-fine">{t('public.footer.credit')}</p>
            <p className="arcana__footer-fine">{t('public.footer.rights', { year: new Date().getFullYear() })}</p>
        </footer>
    )
}
